'use client';

import { CustomFormApi, Form, useFormState } from 'informed';
import { useRouter, useSearchParams } from 'next/navigation';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AnalyticsEventName } from '@/api/analytics';
import {
  ConfirmLogin as ConfirmLoginInterface,
  LoginForm as LoginFormInterface,
} from '@/api/auth';
import { Avatar } from '@/components/avatar';
import { Button, ButtonClose } from '@/components/buttons';
import { ContinueWithGoogle } from '@/components/ContinueWithGoogle';
import { ErrorGroup } from '@/components/form/groups/ErrorGroup';
import { FormGroup } from '@/components/form/groups/FormGroup';
import { Input } from '@/components/form/Input';
import { Password } from '@/components/form/Password';
import { getActiveAvatar } from '@/helpers/avatar';
import { ModalsList } from '@/helpers/breadcrumbModal';
import { isExternalURL } from '@/helpers/common';
import { AUTH_RATE_LIMIT_LOGIN_ATTEMPTS } from '@/helpers/constants';
import { mailMask } from '@/helpers/masks';
import { ModalQueryByName, Modals } from '@/helpers/modals';
import { getUserData, obscureEmail } from '@/helpers/userData';
import { required, validateEmail } from '@/helpers/validators';
import { useAddBreadcrumbModal } from '@/hooks/addBreadcrumbModal';
import { useAnalytics } from '@/hooks/analytics';
import { useLogin } from '@/hooks/login';
import { useNavigation } from '@/hooks/navigation';
import { useCommonStore, useModalsStore } from '@/store';
import { REASON_FOR_UPDATING_USER } from '@/store/commonStore/slices/auth/helpers';

import {
  ScErrorLink,
  ScLogin,
  ScLoginContent,
  ScLoginInfo,
  ScLoginWrapButtons,
} from './styled';

const LOGIN_2FA_ERROR = 'one_time_password';

const Login: FC = () => {
  const { t } = useTranslation('common');
  const close = useModalsStore.use.close();

  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const searchParams = useSearchParams();

  const userLocalStorageData = useMemo(() => getUserData(), []);

  const [isWelcomeBack, setIsWelcomeBack] = useState(
    !!userLocalStorageData?.email
  );

  useAddBreadcrumbModal(ModalsList.LOGIN);

  useEffect(() => {
    const redirect = searchParams.get('redirect');
    redirect && useCommonStore.setState({ afterLoginRedirectUrl: redirect });
  }, [searchParams]);

  const handleCloseModal = () => {
    close({ useCustomOnClose: true });
  };

  return (
    <ScLogin>
      <ScLoginContent>
        <ButtonClose
          id="closeModal"
          gray
          top={isMobileDevice ? 16 : 12}
          right={isMobileDevice ? 16 : 12}
          onClick={handleCloseModal}
        />
        <h2>{t('auth.login.title')}</h2>

        <ContinueWithGoogle
          analyticsEventName={
            isWelcomeBack
              ? AnalyticsEventName.MODAL__WELCOME_BACK__GOOGLE_AUTH_BUTTON_CLICK
              : AnalyticsEventName.MODAL__LOGIN__GOOGLE_AUTH_BUTTON_CLICK
          }
        />

        {isWelcomeBack && (
          <>
            {userLocalStorageData?.avatarId !== undefined && (
              <div className="avatar">
                <Avatar
                  {...getActiveAvatar(userLocalStorageData?.avatarId).avatar}
                />
              </div>
            )}
            <span>{obscureEmail(userLocalStorageData?.email || '')}</span>
          </>
        )}

        <LoginForm
          email={userLocalStorageData?.email || ''}
          isWelcomeBack={isWelcomeBack}
          onLogin={() => setIsWelcomeBack(false)}
        />
      </ScLoginContent>
    </ScLogin>
  );
};

interface LoginFormProps {
  onLogin: () => void;
  isWelcomeBack: boolean;
  email?: string;
}

const LoginForm: FC<LoginFormProps> = ({ email, onLogin, isWelcomeBack }) => {
  const { t } = useTranslation('common');
  const open = useModalsStore.use.open();
  const close = useModalsStore.use.close();
  const { push } = useRouter();

  const insertUser = useCommonStore.use.insertUser();
  const { navigate } = useNavigation();
  const formApiRef =
    useRef<CustomFormApi<LoginFormInterface | ConfirmLoginInterface>>();
  const afterLoginRedirectUrl = useCommonStore.use.afterLoginRedirectUrl();

  const [is2FA, setIs2FA] = useState(false);

  const { handleLogin, userData, loginError, loginPending, requestCount } =
    useLogin();

  const handleSubmit = useCallback(
    async (values: LoginFormInterface | ConfirmLoginInterface) => {
      await handleLogin({
        ...values,
        ...(!is2FA && isWelcomeBack && !!email && { email: email }),
      });
    },
    [handleLogin, isWelcomeBack, email, is2FA]
  );

  useEffect(() => {
    if (userData == null || userData.id == null) return;

    (async () => {
      await insertUser(userData, REASON_FOR_UPDATING_USER.LOGIN);

      if (!afterLoginRedirectUrl) return close();

      isExternalURL(afterLoginRedirectUrl)
        ? window.location.replace(afterLoginRedirectUrl)
        : push(afterLoginRedirectUrl);
      useCommonStore.setState({ afterLoginRedirectUrl: null });
    })();
  }, [userData, insertUser, close, afterLoginRedirectUrl, push]);

  const errorForm = useMemo(() => {
    if (loginError == null || loginError == null) return [];

    let accError: { field: string; message: string }[] = [];

    if (loginError != null) {
      const l = Object.keys(loginError).map((key) => ({
        field: key,
        message: Object.values(loginError[key]),
      }));

      accError = [...accError, ...l] as { field: string; message: string }[];

      if (
        AUTH_RATE_LIMIT_LOGIN_ATTEMPTS != null &&
        requestCount >= AUTH_RATE_LIMIT_LOGIN_ATTEMPTS
      ) {
        const lockedAccountError = {
          field: 'account',
          message: `${t('auth.login.error.lockedAccount')}`,
        };

        accError = [...accError, lockedAccountError];
      }
    }

    return accError.filter(({ message }) => !message.includes(LOGIN_2FA_ERROR));
  }, [loginError, t, requestCount]);

  const lockedAccountError = useMemo(
    () => errorForm.find((e) => e.field === 'account'),
    [errorForm]
  );

  useEffect(() => {
    if (!loginError) return;
    const message = loginError.required?.[0];
    setIs2FA(message?.includes(LOGIN_2FA_ERROR));
  }, [loginError]);

  const handleOpenRecovery = () => {
    const formEmail = formApiRef.current?.getValue('email');
    const recoveryEmail = formEmail ?? email ?? '';
    const emailQuery = encodeURIComponent(recoveryEmail as string);

    if (recoveryEmail) {
      return navigate(
        `?modal=${ModalQueryByName.Recovery}&email=${emailQuery}`
      );
    }
    open(Modals.Recovery);
  };

  return (
    <Form<LoginFormInterface | ConfirmLoginInterface>
      onSubmit={({ values }) => handleSubmit(values)}
      formApiRef={formApiRef}
    >
      {is2FA ? (
        <>
          <FormGroup title={t('auth.login.form.enterOneTimeCode')}>
            <Input
              validateOn="blur"
              name="otp_attempt"
              validate={required}
              placeholder={t('auth.login.form.enterCode')}
            />
          </FormGroup>
          <ScLoginWrapButtons>
            <Button type="submit" primary large isLoading={loginPending}>
              {t('button.continue')}
            </Button>
          </ScLoginWrapButtons>
          <ScLoginInfo>
            <Trans
              ns="common"
              i18nKey="auth.login.infoText"
              components={{
                1: (
                  <Button
                    className="login-btn"
                    link
                    onClick={() => open(Modals.Registration)}
                  />
                ),
              }}
            />
          </ScLoginInfo>
        </>
      ) : (
        <>
          <Analytics />
          {!isWelcomeBack && (
            <FormGroup title={t('auth.registration.form.email')}>
              <Input
                validateOn="blur"
                placeholder={t('auth.registration.form.email')}
                name="email"
                parser={mailMask}
                validate={(value, values) => validateEmail(value, values)}
              />
            </FormGroup>
          )}
          <FormGroup title={t('auth.registration.form.password')}>
            <Password validateOn="blur" name="password" validate={required} />
          </FormGroup>
          {!!errorForm.length && (
            <ErrorGroup>
              {lockedAccountError?.message ?? (
                <Trans
                  i18nKey="auth.login.error.invalidCredentialsV2"
                  components={{
                    1: <ScErrorLink onClick={handleOpenRecovery} />,
                  }}
                />
              )}
            </ErrorGroup>
          )}

          <ScLoginWrapButtons>
            <Button type="submit" primary large wide isLoading={loginPending}>
              {t('auth.login.buttons.login')}
            </Button>
            <Button link className="recovery-btn" onClick={handleOpenRecovery}>
              {t('auth.login.form.forgotPassword')}
            </Button>
          </ScLoginWrapButtons>
          <ScLoginInfo>
            {isWelcomeBack ? (
              <Button link className="login-btn" onClick={onLogin}>
                <span>{t('loginDifferentAccount')}</span>
              </Button>
            ) : (
              <Trans
                ns="common"
                i18nKey="auth.login.infoText"
                components={{
                  1: (
                    <Button
                      className="login-btn"
                      link
                      onClick={() => open(Modals.Registration)}
                    />
                  ),
                }}
              />
            )}
          </ScLoginInfo>
        </>
      )}
    </Form>
  );
};

const Analytics = () => {
  const { sendEventToAnalytics } = useAnalytics();

  const { touched } = useFormState();
  const touchedForm = Object.values(touched).some((t) => t);

  useEffect(() => {
    sendEventToAnalytics({ name: AnalyticsEventName.LOGIN_APPEARANCE });
  }, [sendEventToAnalytics]);

  useEffect(() => {
    if (touchedForm) {
      sendEventToAnalytics({ name: AnalyticsEventName.LOGIN_TOUCHED });
    }
  }, [touchedForm, sendEventToAnalytics]);

  return null;
};

export default Login;
